import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, of, tap } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  BaseApiService,
  CacheService,
  ElementDirectionService,
  TranslateLanguage,
  translateLanguages,
  TranslateResponseDto,
} from '@fleet-beat/core';

@Injectable({
  providedIn: 'root',
})
export class TranslateService {
  get currentLanguage(): string {
    return this.targetLanguage;
  }

  private myMemoryKey: string = '7c5b845d2c26dccdcbe3';
  private targetLanguage: string;

  private readonly baseUrl: string = `https://api.mymemory.translated.net`;
  private readonly username: string = 'roman.kostiv@it-dimension.com';
  private readonly password: string = '1q2w3e!Q@W#E';
  private readonly langKey: string = 'lang';

  constructor(
    private http: HttpClient,
    private baseApiService: BaseApiService,
    private cacheService: CacheService,
    private elementDirectionService: ElementDirectionService,
  ) {
    this.initWithLanguage();
  }

  getKeyGenForTranslate(): void {
    this.http
      .get<string>(`${this.baseUrl}/keygen?user=${this.username}&pass=${this.password}`)
      .pipe(tap((res: string) => (this.myMemoryKey = res)))
      .subscribe();
  }

  setLanguage(langCode: string): void {
    localStorage.setItem(this.langKey, langCode);
    this.targetLanguage = langCode;
    this.elementDirectionService.setDirectionByLanguage(langCode);
  }

  getTranslateText(text: string): Observable<TranslateResponseDto> {
    if (!this.targetLanguage || !text || this.targetLanguage === 'en') {
      return of({
        responseData: {
          match: 1,
          translatedText: text,
        },
      } as TranslateResponseDto);
    }

    const params: Record<'q' | 'langpair' | 'key', string> = {
      q: text,
      langpair: `en|${this.targetLanguage}`,
      key: this.myMemoryKey,
    };

    const cacheKey: string = 'translate';
    const wordKey: string = `${this.targetLanguage}-${text}`;
    const cacheData: Record<string, TranslateResponseDto> =
      this.cacheService.getItem<Record<string, TranslateResponseDto>>(cacheKey);
    if (cacheData && cacheData[wordKey]) {
      return of(cacheData[wordKey]);
    }

    return this.baseApiService.get<TranslateResponseDto>(`${this.baseUrl}/get`, { params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: TranslateResponseDto) => this.matchTranslation(response, text)),
      tap((response: TranslateResponseDto) => this.saveTranslationToCache(response, cacheKey, wordKey)),
    );
  }

  matchTranslation(response: TranslateResponseDto, textToTranslate: string): TranslateResponseDto {
    const matches = response.matches;
    if (!matches || !matches.length) {
      return response;
    }

    if (response.responseData.translatedText !== textToTranslate) {
      return response;
    }

    const match: TranslateResponseDto['matches'][0] =
      matches.find((match: TranslateResponseDto['matches'][0]) => match.source === 'en-US') || matches[0];

    response.responseData.translatedText = match.translation;
    return response;
  }

  private saveTranslationToCache(response: TranslateResponseDto, cacheKey: string, wordKey: string): void {
    const data: Record<string, TranslateResponseDto> =
      this.cacheService.getItem<Record<string, TranslateResponseDto>>(cacheKey) || {};

    data[wordKey] = response;
    this.cacheService.setItem<Record<string, TranslateResponseDto>>(cacheKey, data);
  }

  private initWithLanguage(): void {
    const localStorageLang: string = localStorage.getItem(this.langKey);
    if (localStorageLang) {
      this.setLanguage(localStorageLang);
      return;
    }

    const availableToTranslateLanguageCodes: string[] = translateLanguages.map((lang: TranslateLanguage) => lang.code);
    const browserLanguages: Readonly<string[]> = navigator.languages;
    const browserLanguage: string = browserLanguages.find((lang: string) => {
      const langCode: string = this.getLangCode(lang);
      return availableToTranslateLanguageCodes.includes(langCode);
    });

    this.setLanguage(browserLanguage ? this.getLangCode(browserLanguage) : 'en');
  }

  // NOTE en-US -> en
  private getLangCode(lang: string): string {
    return lang.split('-')[0];
  }
}
